import { fetchConfig } from '@theme/utilities';

/**
 * Corporate gift builder: multiple collections with per-card recipient counts,
 * optional services multiplied by all recipients. Branding notes save to cart.note.
 */
class CorporateGiftBuilder extends HTMLElement {
  connectedCallback() {
    this.#groupBlocks();

    this.form = this.querySelector('[data-gift-form]');
    this.serviceInputs = this.querySelectorAll('[data-gift-service]');
    this.perEl = this.querySelector('[data-gift-per]');
    this.perRow = this.querySelector('[data-gift-per-row]');
    this.totalEl = this.querySelector('[data-gift-total]');
    this.submit = this.querySelector('[data-gift-submit]');
    this.submitLabel = this.querySelector('[data-gift-submit-label]');
    this.errorEl = this.querySelector('[data-gift-error]');
    this.noteInput = this.querySelector('[data-gift-note]');
    this.minRecipients = Number(this.dataset.minRecipients || 1);
    this.moneyFormat = this.dataset.moneyFormat || '';
    this.idleLabel = this.submitLabel?.textContent || '';

    this.#preselectTier();
    this.form?.addEventListener('input', () => this.#update());
    this.form?.addEventListener('change', () => this.#update());
    this.addEventListener('quantity-selector:update', () => {
      this.#update();
      this.#scrollTierIntoView();
    });
    this.form?.addEventListener('submit', (event) => this.#onSubmit(event));
    this.#update();
    this.#syncTierCarousel();
  }

  #isServiceNode(node) {
    return (
      node instanceof HTMLElement &&
      (node.matches('.corporate-gift-service') || Boolean(node.querySelector(':scope > .corporate-gift-service')))
    );
  }

  #groupBlocks() {
    const carousel = this.querySelector('[data-gift-tier-carousel]');
    const servicesHeading = this.querySelector('.corporate-gift-builder__step-heading--services');
    if (!carousel || !servicesHeading) return;

    const services = Array.from(carousel.children).filter((node) => this.#isServiceNode(node));
    if (services.length) servicesHeading.after(...services);
  }

  #syncTierCarousel() {
    const carousel = this.querySelector('[data-gift-tier-carousel]');
    const indicator = this.querySelector('[data-gift-tier-indicator]');
    const thumb = this.querySelector('[data-gift-tier-thumb]');
    if (!carousel) return;

    const updateIndicator = () => {
      if (!indicator || !thumb) return;

      const max = carousel.scrollWidth - carousel.clientWidth;
      if (max <= 1) {
        indicator.hidden = true;
        return;
      }

      indicator.hidden = false;
      const thumbWidth = Math.max((carousel.clientWidth / carousel.scrollWidth) * 100, 16);
      const left = (carousel.scrollLeft / max) * (100 - thumbWidth);
      thumb.style.width = `${thumbWidth}%`;
      thumb.style.left = `${left}%`;
    };

    carousel.addEventListener('scroll', updateIndicator, { passive: true });
    window.addEventListener('resize', updateIndicator, { passive: true });
    this.#scrollTierIntoView();
    updateIndicator();
  }

  #scrollTierIntoView() {
    const carousel = this.querySelector('[data-gift-tier-carousel]');
    if (!carousel) return;

    const selected = this.querySelector('.corporate-gift-tier--selected');
    const slide = selected?.closest('[data-gift-tier-carousel] > *');
    if (!slide || carousel.clientWidth >= carousel.scrollWidth - 1) return;

    const slideRect = slide.getBoundingClientRect();
    const carouselRect = carousel.getBoundingClientRect();
    const padding = Number.parseFloat(getComputedStyle(carousel).paddingInlineStart) || 0;
    carousel.scrollLeft += slideRect.left - carouselRect.left - padding;
  }

  #preselectTier() {
    const handle = new URLSearchParams(window.location.search).get('tier');
    if (!handle) return;

    const match = this.querySelector(`[data-tier-handle="${CSS.escape(handle)}"]`);
    if (!match || match.dataset.available !== 'true') return;

    const input = match.querySelector('[data-gift-tier-qty]');
    if (!input) return;

    input.value = String(this.minRecipients);
    input.dataset.previousValue = input.value;
    input.closest('quantity-selector-component')?.updateButtonStates?.();
  }

  #tierCards() {
    return Array.from(this.querySelectorAll('[data-gift-tier]'));
  }

  #tierState(card) {
    const qtyInput = card.querySelector('[data-gift-tier-qty]');
    const qty = Math.max(Number(qtyInput?.value || 0), 0);
    const available = card.dataset.available === 'true';

    return {
      card,
      qtyInput,
      qty,
      available,
      variantId: Number(card.dataset.variantId || 0),
      priceCents: Number(card.dataset.priceCents || 0),
      title: card.dataset.tierTitle || '',
      selected: available && qty >= this.minRecipients,
    };
  }

  #selectedTiers() {
    return this.#tierCards()
      .map((card) => this.#tierState(card))
      .filter((tier) => tier.selected);
  }

  #normalizeTierQuantity(input) {
    if (!(input instanceof HTMLInputElement)) return;

    const value = Math.max(Number(input.value || 0), 0);
    const previous = Number(input.dataset.previousValue || 0);
    const min = this.minRecipients;
    let next = value;

    if (value > 0 && value < min) {
      next = value > previous ? min : 0;
    }

    if (next !== value) {
      input.value = String(next);
      input.closest('quantity-selector-component')?.updateButtonStates?.();
    }

    input.dataset.previousValue = input.value;
  }

  #cents(input) {
    return Number(input?.dataset.priceCents || 0);
  }

  #formatMoney(cents) {
    const value = (Number(cents) / 100).toFixed(2);
    if (!this.moneyFormat) return '$' + value;

    const withComma = value.replace('.', ',');
    const noDecimals = String(Math.round(Number(cents) / 100));
    const open = '{' + '{';
    const close = '}' + '}';
    const format = this.moneyFormat.replaceAll(' ', '');

    return format
      .replaceAll(open + 'amount_with_comma_separator' + close, withComma)
      .replaceAll(open + 'amount_no_decimals' + close, noDecimals)
      .replaceAll(open + 'amount_with_apostrophe_separator' + close, value.replace('.', "'"))
      .replaceAll(open + 'amount' + close, value);
  }

  #update() {
    this.querySelectorAll('[data-gift-tier-qty]').forEach((input) => this.#normalizeTierQuantity(input));

    const selected = this.#selectedTiers();
    let serviceCents = 0;
    this.serviceInputs.forEach((input) => {
      if (input.checked) serviceCents += this.#cents(input);
    });

    const recipients = selected.reduce((sum, tier) => sum + tier.qty, 0);
    const collectionsTotal = selected.reduce((sum, tier) => sum + tier.priceCents * tier.qty, 0);
    const total = collectionsTotal + serviceCents * recipients;

    this.#tierCards().forEach((card) => {
      const state = this.#tierState(card);
      card.classList.toggle('corporate-gift-tier--selected', state.selected);
    });

    if (selected.length === 1) {
      const perRecipient = selected[0].priceCents + serviceCents;
      if (this.perEl) this.perEl.textContent = this.#formatMoney(perRecipient);
      this.perRow?.classList.remove('hidden');
    } else {
      this.perRow?.classList.add('hidden');
    }

    if (this.totalEl) this.totalEl.textContent = this.#formatMoney(total);

    const valid = selected.length > 0;
    if (this.submit) this.submit.disabled = !valid || this.dataset.busy === 'true';
  }

  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.classList.toggle('hidden', !message);
    if (message) this.errorEl.focus();
  }

  #cartAddUrl() {
    return Theme.routes.cart_add_url || this.dataset.cartAddUrl || '/cart/add.js';
  }

  #cartUpdateUrl() {
    return Theme.routes.cart_update_url || this.dataset.cartUpdateUrl || '/cart/update.js';
  }

  /** Same payload and endpoint as `special-instruction.js` / cart.note. */
  async #saveCartNote() {
    const note = this.noteInput?.value?.trim();
    if (!note) return;

    const config = fetchConfig('json', {
      body: JSON.stringify({ note }),
    });

    const response = await fetch(this.#cartUpdateUrl(), config);
    if (!response.ok) throw new Error('cart-note');
  }

  async #onSubmit(event) {
    event.preventDefault();
    if (this.dataset.busy === 'true') return;

    const selected = this.#selectedTiers();
    if (!selected.length) return;

    const recipients = selected.reduce((sum, tier) => sum + tier.qty, 0);
    const properties = { _corporate_gift: '1' };
    const items = selected.map((tier) => ({
      id: tier.variantId,
      quantity: tier.qty,
      properties: {
        ...properties,
        Collection: tier.title,
      },
    }));

    this.serviceInputs.forEach((input) => {
      if (!input.checked || !input.value) return;
      items.push({
        id: Number(input.value),
        quantity: recipients,
        properties: {
          ...properties,
          Service: input.dataset.serviceTitle || '',
        },
      });
    });

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    this.dataset.busy = 'true';
    this.#showError('');
    if (this.submit) this.submit.disabled = true;
    if (this.submitLabel) this.submitLabel.textContent = this.dataset.addingLabel || this.idleLabel;

    try {
      await this.#saveCartNote();

      const response = await fetch(this.#cartAddUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
        }),
      });
      const payload = await response.json();

      if (payload.status) {
        this.#showError(payload.description || payload.message || this.dataset.errorLabel);
        return;
      }

      const redirect = this.dataset.redirect === 'checkout' ? '/checkout' : this.dataset.cartUrl || '/cart';
      window.location.href = redirect;
    } catch (error) {
      this.#showError(this.dataset.errorLabel);
    } finally {
      this.dataset.busy = 'false';
      if (this.submitLabel) this.submitLabel.textContent = this.idleLabel;
      this.#update();
    }
  }
}

if (!customElements.get('corporate-gift-builder')) {
  customElements.define('corporate-gift-builder', CorporateGiftBuilder);
}
